<script lang="ts" setup>
import freeAdmission from '@/assets/images/free-admission.webp';
import kanagawaHeroBg from '@/assets/images/kanagawa-hero-bg.webp';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

const { trackEvent } = useGtm();
const profileStore = useProfileStore();

const config = ACTIVE_CAMPAIGN;

const isCampaignActive = computed(() => {
	if (!config.isActive) return false;

	const now = dayjs().tz(config.timezone);
	const endDate = dayjs.tz(config.campaignEndDate, config.timezone);

	return now.isBefore(endDate);
});

function handleBannerClick() {
	trackEvent({
		event: GTM_EVENT_NAME.VIEW_FEATURED_EVENT,
		user_id: profileStore.profileId,
		featured_event_name: config.gtmEventName,
		section: 'visiting-japan-featured-banner',
	});
}
</script>

<template>
	<div
		v-if="isCampaignActive"
		class="kanagawa-featured-banner"
	>
		<div class="banner-container">
			<h2 class="banner-title text-left">
				{{ config.title }}
			</h2>
			<p class="banner-description text-left">
				{{ config.description }}
			</p>
			<a
				:href="config.url"
				class="banner-link"
				target="_blank"
				rel="noopener noreferrer"
				@click="handleBannerClick"
			>
				<section class="banner-section">
					<img
						:src="kanagawaHeroBg"
						alt=""
						class="banner-background"
					>
					<img
						:src="freeAdmission"
						alt="Free Admission"
						class="free-admission-badge"
					>
					<div class="banner-content">
						<h1 class="calligraphy-title">
							<img
								src="@/assets/svg/kanagawa-hero-title.svg"
								alt="KANAGAWA Block Party — A Japanese Art & Culture Experience"
								class="calligraphy-image"
							>
						</h1>
						<div class="green-overlay">
							<p class="overlay-title">
								{{ config.overlayTitle }}
							</p>
							<p class="overlay-subtitle">
								{{ config.overlaySubtitle }}
							</p>
						</div>
					</div>
				</section>
			</a>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.kanagawa-featured-banner {
	padding: 0;
	margin-bottom: spacings-get(12);

	@include media-query-max(mobile) {
		margin-bottom: spacings-get(6);
	}
}

.banner-container {
	width: 100%;
}

.banner-title {
	@include fonts-get(bold, display-sm);
	color: #4b4b4b;
	margin-bottom: spacings-get(2);

	@include media-query-max(mobile) {
		@include fonts-get(bold, display-xs);
	}
}

.banner-description {
	@include fonts-get(regular, text-lg);
	color: colors-get(gray, 800);
	margin-bottom: spacings-get(4);

	@include media-query-max(mobile) {
		@include fonts-get(regular, text-md);
		margin-bottom: spacings-get(3);
	}
}

.banner-link {
	display: block;
	text-decoration: none;
	cursor: pointer;
	transition: transform 0.3s ease;

	&:hover {
		transform: translateY(-4px);
	}
}

.banner-section {
	position: relative;
	overflow: hidden;
	@include border-radius-lg;
}

.banner-background {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.free-admission-badge {
	position: absolute;
	top: 0;
	right: spacings-get(4);
	z-index: 20;
	width: rem(96);
	height: auto;
	filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3));

	@include media-query-max(mobile) {
		width: rem(48);
	}
}

.banner-content {
	position: relative;
	z-index: 10;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: spacings-get(6) spacings-get(4) spacings-get(16);

	@include media-query-max(mobile) {
		padding: spacings-get(4) spacings-get(4) spacings-get(12);
	}
}

.calligraphy-title {
	width: 100%;
	max-width: rem(800);
	margin: 0;

	@include media-query-max(mobile) {
		max-width: rem(650);
	}
}

.calligraphy-image {
	width: 100%;
	height: auto;
	display: block;
}

.green-overlay {
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	width: 100%;
	background: #30af60;
	padding: spacings-get(2) spacings-get(4);
	text-align: center;
	z-index: 5;

	@include media-query-max(mobile) {
		padding: spacings-get(2) spacings-get(3);
	}
}

.overlay-title {
	color: colors-get(base, white);
	@include fonts-get(bold, text-xl);
	margin: 0;
	line-height: 1.2;

	@include media-query-max(mobile) {
		@include fonts-get(bold, text-md);
	}
}

.overlay-subtitle {
	color: rgba(255, 255, 255, 0.9);
	@include fonts-get(regular, text-md);
	margin: 0;
	line-height: 1.3;

	@include media-query-max(mobile) {
		@include fonts-size(text-xs);
	}
}
</style>
